import React, { useState, useEffect } from "react";
import { useCompany } from "../../Context/CompanyContext";
import serviciosData from "../../Data/ServiciosData";
import { fetchWithAuth } from "../../Context/UserContext";
import API_URL from "../../config";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { X } from "lucide-react";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

const chipClass = (selected) =>
  cn(
    "rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted",
    selected && "border-primary bg-primary/10 text-primary"
  );

const Servicios = () => {
  const { companies, fetchCompanies } = useCompany();
  const [selectedServicios, setSelectedServicios] = useState([]);
  const [selectedProductos, setSelectedProductos] = useState([]);
  const [newService, setNewService] = useState("");
  const [newProduct, setNewProduct] = useState("");
  const id_company = companies?.company?.id_company;

  // Inicializa los valores guardados
  useEffect(() => {
    if (companies?.profile?.servicios || companies?.profile?.productos) {
      // Si hay servicios y productos guardados, los inicializamos
      setSelectedServicios(companies.profile.servicios || []);
      setSelectedProductos(companies.profile.productos || []);
    }
  }, [companies]);

  const handleAddCustomService = () => {
    if (newService && !selectedServicios.includes(newService)) {
      setSelectedServicios([...selectedServicios, newService]);
      setNewService("");
      toast.success("Servicio agregado correctamente.");
    }
  };

  const handleAddCustomProduct = () => {
    if (newProduct && !selectedProductos.includes(newProduct)) {
      setSelectedProductos([...selectedProductos, newProduct]);
      setNewProduct("");
      toast.success("Producto agregado correctamente.");
    }
  };

  const handleSaveChangesClick = async () => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/bs/company-profile/${id_company}/update-servicios`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            profile: {
              servicios: selectedServicios,
              productos: selectedProductos,
            },
          }),
        }
      );

      if (response.ok) {
        toast.success("Servicios y productos actualizados exitosamente.");
        fetchCompanies(); // Actualiza la lista de empresas
      } else {
        const data = await response.json();
        toast.error(`Error al actualizar servicios: ${data.error}`);
      }
    } catch (error) {
      console.error("Error en la actualización de servicios:", error);
      toast.error("Error al actualizar servicios.");
    }
  };

  const handleRemoveService = (service) => {
    setSelectedServicios(selectedServicios.filter((item) => item !== service));
  };

  const handleRemoveProduct = (product) => {
    setSelectedProductos(selectedProductos.filter((item) => item !== product));
  };

  const sector = companies?.company?.sector;
  const sectorData = sector ? serviciosData[sector] : null;

  return (
    <Card>
      <CardContent className="space-y-6 pt-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Servicios y Productos Ofrecidos</h3>
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Sector:</strong> {sector}
          </p>
        </div>

        {/* Productos sugeridos */}
        <div>
          <label className="mb-2 block text-sm font-medium text-foreground">Productos que brinda la empresa:</label>
          <div className="flex flex-wrap gap-2">
            {sectorData?.productos.map((producto, index) => (
              <button
                key={index}
                type="button"
                onClick={() =>
                  selectedProductos.includes(producto)
                    ? handleRemoveProduct(producto)
                    : setSelectedProductos([...selectedProductos, producto])
                }
                className={chipClass(selectedProductos.includes(producto))}
              >
                {producto}
              </button>
            ))}
          </div>

          <div className="mt-2 flex gap-2">
            <Input
              type="text"
              placeholder="Nuevo producto"
              value={newProduct}
              onChange={(e) => setNewProduct(e.target.value)}
              maxLength={50}
            />
            <Button type="button" variant="outline" onClick={handleAddCustomProduct}>
              OK
            </Button>
          </div>

          {selectedProductos.length > 0 && (
            <ul className="mt-2 space-y-1">
              {selectedProductos.map((producto, index) => (
                <li key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                  {producto}
                  <button onClick={() => handleRemoveProduct(producto)} className="hover:text-destructive">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Servicios sugeridos */}
        <div>
          <label className="mb-2 block text-sm font-medium text-foreground">Servicios que brinda la empresa:</label>
          <div className="flex flex-wrap gap-2">
            {sectorData?.servicios.map((servicio, index) => (
              <button
                key={index}
                type="button"
                onClick={() =>
                  selectedServicios.includes(servicio)
                    ? handleRemoveService(servicio)
                    : setSelectedServicios([...selectedServicios, servicio])
                }
                className={chipClass(selectedServicios.includes(servicio))}
              >
                {servicio}
              </button>
            ))}
          </div>

          <div className="mt-2 flex gap-2">
            <Input
              type="text"
              placeholder="Agrega un servicio diferente (max 50 caracteres)"
              value={newService}
              onChange={(e) => setNewService(e.target.value)}
              maxLength={50}
            />
            <Button type="button" variant="outline" onClick={handleAddCustomService}>
              OK
            </Button>
          </div>

          {selectedServicios.length > 0 && (
            <ul className="mt-2 space-y-1">
              {selectedServicios.map((servicio, index) => (
                <li key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                  {servicio}
                  <button onClick={() => handleRemoveService(servicio)} className="hover:text-destructive">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <Button onClick={handleSaveChangesClick}>Guardar Cambios</Button>
      </CardContent>
    </Card>
  );
};

export default Servicios;
